'use client';

import { useLayoutEffect, type RefObject } from 'react';

const properties = ['--background', '--foreground', '--popover', '--popover-foreground', '--primary', '--primary-foreground', '--muted', '--muted-foreground', '--accent', '--accent-foreground', '--border', '--ring', '--destructive', '--field-border', '--field-surface', '--field-error', '--field-focus-shadow', '--dropdown-surface', '--dropdown-shadow', '--dropdown-radius', '--dropdown-row-radius', '--font-ui', '--text-sm', '--text-xs', '--leading-ui', '--community-bg', '--community-fg', '--community-dim', '--community-muted', '--community-border'];

/** Portals inherit the trigger's local theme, typography and reading direction. */
export function useDropdownTheme(anchor: RefObject<HTMLElement | null>, popup: HTMLElement | null) {
  useLayoutEffect(() => {
    const trigger = anchor.current;
    if (!trigger || !popup) return;
    const element = popup.ownerDocument.getElementById(popup.id);
    if (!element) return;
    const update = () => {
      const theme = getComputedStyle(trigger);
      for (const property of properties) element.style.setProperty(property, theme.getPropertyValue(property));
      element.style.fontFamily = theme.fontFamily;
      element.dir = theme.direction;
    };
    update();
    const observer = new MutationObserver(update);
    for (let node: HTMLElement | null = trigger; node; node = node.parentElement) observer.observe(node, { attributes: true, attributeFilter: ['class', 'style', 'dir'] });
    return () => observer.disconnect();
  }, [anchor, popup]);
}

/** Keep Radix's positioning wrapper above native modal dialogs and clipping. */
export function useDropdownTopLayer(popup: HTMLElement | null, container: HTMLElement | null | undefined) {
  useLayoutEffect(() => {
    const wrapper = popup?.parentElement;
    if (!container || !wrapper?.hasAttribute('data-radix-popper-content-wrapper') || typeof wrapper.showPopover !== 'function') return;
    wrapper.setAttribute('popover', 'manual');
    Object.assign(wrapper.style, { margin: '0', border: '0', padding: '0', overflow: 'visible', background: 'transparent' });
    wrapper.showPopover();
    return () => { if (wrapper.matches(':popover-open')) wrapper.hidePopover(); };
  }, [popup, container]);
}
